import { computed, nextTick } from "vue";
import type { LocationQuery } from "vue-router";
import { useRoute, useRouter } from "vue-router";

import { isOwnOpenDraft } from "@/components/Thread/thread-status";
import { beginDraftSwitch } from "@/composables/draft-switch";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { flushAllSettled } from "@/composables/pending-flush";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";
import { type ThreadSchema, useThreadsStore } from "@/stores/threads-store";
import { useUserManager } from "@/stores/user-store";

// `draft` is the shareable URL identity. Accept the old `thread` query as a
// compatibility alias so existing bookmarks still open, but only write `draft`.
export const draftIdFromQuery = (query: LocationQuery) => {
    const id = query.draft ?? query.thread;
    return typeof id === "string" ? id : undefined;
};

export const navigationLeavesDraft = (
    from: LocationQuery[string],
    to: LocationQuery[string]
) => {
    const left = typeof from === "string" ? from : "";
    return Boolean(left) && to !== left;
};

export const useThreadBranchView = () => {
    const route = useRoute();
    const router = useRouter();
    const releaseStore = useReleaseStore();
    const threadsStore = useThreadsStore();
    const { stopEditing } = useEditStore();
    const { isViewOnly } = useEditManager();
    const syncStore = useSyncStore();
    const { user } = useUserManager();

    const viewingThreadBranch = computed(
        () => releaseStore.activeThreadId !== null
    );

    const threadRecord = (id: string) => {
        const active = threadsStore.activeThread;
        if (active?.identity === id) return active;
        return threadsStore.threads.find((t) => t.identity === id);
    };

    const branchThread = computed(() => {
        const id = releaseStore.activeThreadId;
        return id ? threadRecord(id) : undefined;
    });

    // `x-thread-revision` is attached when a request is SENT, not when it is
    // queued, so a pending edit that outlives this draft's revision lands on
    // the working copy. The order is load-bearing: editors commit their text
    // on teardown, and the watchers that trigger queue on the next tick.
    const drainPendingEdits = async () => {
        stopEditing();
        await nextTick();
        await flushAllSettled();
        await syncStore.sync();
    };

    const clearDraftContext = () => {
        const wasViewing = releaseStore.activeThreadId !== null;
        releaseStore.clearOpenThreadId();
        if (wasViewing) beginDraftSwitch("Leaving draft…");
        const {
            thread: _omitThread,
            draft: _omitDraft,
            ...query
        } = route.query;
        void router.replace({ query });
    };

    // Only the draft's own author can ever delete it (delete_thread.py) — so a
    // non-author leaving one is never going to succeed, whatever it holds.
    // Skip the call entirely rather than let the backend's expected refusal
    // surface as a global 403 (see client.ts's unauthorized-access handling).
    // A viewer may not delete at all (403), even a draft they authored, so
    // their exit likewise leaves the draft be.
    //
    // For the author, `delete_thread` refuses a draft that holds work; that
    // refusal is the expected answer, not an error.
    const prepareDraftExit = async (known?: ThreadSchema) => {
        const id = releaseStore.activeThreadId;
        await drainPendingEdits();

        let record = branchThread.value ?? known;
        if (id && !record) {
            record =
                (await threadsStore.fetchThreadSummary(id, {
                    withFootprint: false,
                })) ?? undefined;
        }
        let deleted = false;
        if (
            id &&
            !isViewOnly.value &&
            record?.auto_created &&
            isOwnOpenDraft(record, user.value?.identity)
        ) {
            deleted = await threadsStore.deleteThread(id);
        }

        const stillOpen = !record || record.status === "open";
        if (id && !deleted && stillOpen) {
            void threadsStore.refreshThreadFootprint(id);
        }
    };

    const exitThreadBranchView = async (known?: ThreadSchema) => {
        await prepareDraftExit(known);
        clearDraftContext();
    };

    const leaveDraft = async (known?: ThreadSchema) => {
        await exitThreadBranchView(known);
    };

    const leaveIfResolved = (threadId: string) => {
        if (releaseStore.activeThreadId !== threadId) return;
        const record = threadRecord(threadId);
        if (!record || record.status === "open") return;
        void exitThreadBranchView();
    };

    const enterDraft = async (threadId: string) => {
        // Same id is a no-op: the drawer's watcher re-fires this on mount.
        if (releaseStore.activeThreadId === threadId) return;
        await drainPendingEdits();

        releaseStore.setOpenThreadId(threadId);
        // Every revision-watching store refetches from here, and the page keeps
        // showing working-copy content until they land — cover it.
        beginDraftSwitch("Opening draft…");
        if (draftIdFromQuery(route.query) !== threadId || route.query.thread) {
            const { thread: _omitThread, ...query } = route.query;
            void router.push({ query: { ...query, draft: threadId } });
        }
    };

    // Refuse resolved threads and discussions only once the store has the
    // record: a deep link arrives before the list, and "no record yet" is not
    // "not open".
    const activateFromRoute = (threadId: string) => {
        const known = threadsStore.threads.find((t) => t.identity === threadId);
        if (known && (known.status !== "open" || known.is_discussion)) {
            releaseStore.clearOpenThreadId();
            return false;
        }
        releaseStore.setOpenThreadId(threadId);
        return true;
    };

    const goToReconcile = async () => {
        const id = releaseStore.activeThreadId;
        if (!id) return;
        await drainPendingEdits();
        void router.push({ name: "reconcile", query: { draft: id } });
    };

    return {
        viewingThreadBranch,
        branchThread,
        activateFromRoute,
        drainPendingEdits,
        prepareDraftExit,
        enterDraft,
        exitThreadBranchView,
        leaveDraft,
        leaveIfResolved,
        goToReconcile,
    };
};
